import { Injectable } from '@angular/core';
import { extractClaims } from '@umss/shared/utils';
import { BehaviorSubject } from 'rxjs';

import { toSignal } from '@angular/core/rxjs-interop';
import {
  AuthenticationResponseDto,
  AuthState,
  UserDto,
  UserRole
} from '../../../models/auth.model';

export const AUTH_LOCALSTORAGE_KEYS = {
  expAt: 'tokenExpiresAt',
  user: 'profile',
  permissions: 'permissions',
  facilityIds: 'facilityIds',
  lastSessionAt: 'last_session_at'
} as const;

@Injectable({ providedIn: 'root' })
export class AuthStorageService {
  private stateSubject = new BehaviorSubject<AuthState>({
    lastSessionAt: null,
    user: null,
    permissions: [],
    role: null,
    facilityIds: [],
    contractorId: null,
    corporateId: null,
    expiresAt: null
  });

  /** Observable of current authentication state. */
  readonly state$ = this.stateSubject.asObservable();
  /** Returns current authentication state snapshot. */
  get state(): AuthState {
    return this.stateSubject.getValue();
  }

  readonly stateSignal = toSignal(this.state$, {
    initialValue: this.stateSubject.getValue()
  });

  private storageAvailable: boolean;

  constructor() {
    this.storageAvailable = this.checkStorageAvailability();
  }

  decodeAndBuildState(
    accessToken: string | null,
    authenticationData: Partial<AuthenticationResponseDto>
  ): void {
    if (!accessToken || accessToken.trim().length === 0) {
      return;
    }

    const { role, emailVerified, facilityIds, contractorId, exp, corporateId } =
      extractClaims(accessToken);

    const expiresAt =
      authenticationData?.expiresAt ??
      (exp ? new Date(exp * 1000).toISOString() : null);

    const state: AuthState = {
      lastSessionAt:
        this.getItem<string>(AUTH_LOCALSTORAGE_KEYS.lastSessionAt) ??
        new Date().toISOString(),
      user: authenticationData?.user ?? null,
      permissions: authenticationData?.permissions ?? [],
      role: ((role as UserRole | undefined) ??
        authenticationData?.user?.role ??
        null) as UserRole | null,
      facilityIds,
      contractorId,
      corporateId,
      expiresAt
    };

    if (state.user && typeof emailVerified === 'boolean') {
      state.user = { ...state.user, emailVerified };
    }

    this.stateSubject.next(state);
  }

  /** Patches the user profile in the current auth state and in localStorage. */
  updateUser(user: UserDto): void {
    const current = this.stateSubject.getValue();
    this.stateSubject.next({ ...current, user });

    if (this.storageAvailable) {
      try {
        localStorage.setItem(AUTH_LOCALSTORAGE_KEYS.user, JSON.stringify(user));
      } catch {
        // ignore storage errors
      }
    }
  }

  clearState(): void {
    this.stateSubject.next({
      lastSessionAt: null,
      user: null,
      permissions: [],
      role: null,
      facilityIds: [],
      contractorId: null,
      corporateId: null,
      expiresAt: null
    });
  }

  /**
   * Saves an access token, refresh token, and expiration time to localStorage.
   */
  saveData(authResponse: Partial<AuthenticationResponseDto>): void {
    if (!this.storageAvailable) return;

    try {
      if (authResponse.expiresAt) {
        localStorage.setItem(
          AUTH_LOCALSTORAGE_KEYS.expAt,
          authResponse.expiresAt
        );
      }

      if (authResponse.user) {
        localStorage.setItem(
          AUTH_LOCALSTORAGE_KEYS.user,
          JSON.stringify(authResponse.user)
        );
      }

      if (authResponse.facilityAccess?.facilityId) {
        localStorage.setItem(
          AUTH_LOCALSTORAGE_KEYS.facilityIds,
          authResponse.facilityAccess?.facilityId
        );
      }

      if (authResponse.permissions) {
        localStorage.setItem(
          AUTH_LOCALSTORAGE_KEYS.permissions,
          JSON.stringify(authResponse.permissions)
        );
      }

      localStorage.setItem(
        AUTH_LOCALSTORAGE_KEYS.lastSessionAt,
        new Date().toISOString()
      );
    } catch {
      return;
    }
  }

  getItem<T>(key: string): T | undefined {
    if (
      key === AUTH_LOCALSTORAGE_KEYS.user ||
      key === AUTH_LOCALSTORAGE_KEYS.permissions
    ) {
      const stringified = localStorage.getItem(key);
      if (!stringified) return undefined;
      return (JSON.parse(stringified) as T) ?? undefined;
    }
    return (localStorage.getItem(key) as T) || undefined;
  }

  /**
   * Loads authentication data (access token, refresh token, expiration).
   */
  loadTokens(): Partial<AuthenticationResponseDto> {
    if (!this.storageAvailable) {
      return {};
    }

    try {
      return {
        expiresAt: this.getItem<string>(AUTH_LOCALSTORAGE_KEYS.expAt),
        user: this.getItem<UserDto>(AUTH_LOCALSTORAGE_KEYS.user),
        permissions: this.getItem<string[]>(AUTH_LOCALSTORAGE_KEYS.permissions)
      };
    } catch {
      return {};
    }
  }

  /**
   * Clears all authentication-related data from localStorage.
   */
  clearTokens(): void {
    if (!this.storageAvailable) return;

    try {
      Object.values(AUTH_LOCALSTORAGE_KEYS).forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.warn('Failed to clear tokens from localStorage:', error);
    }
  }

  /**
   * Verifies that localStorage is available and writable.
   */
  private checkStorageAvailability(): boolean {
    try {
      const testKey = '__umss_test__';
      localStorage.setItem(testKey, '1');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }
}
